import React, { memo, useContext } from "react";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { FormSection, Field } from "redux-form";

import {
	AddressSectionRedux,
	Badge,
	Button,
	CheckButton,
	CustomInput,
	OverlayTrigger,
	PhoneInputRedux,
	RequiredMarkContext,
	SpinnerLoader,
} from "components";
import MerchantSensitiveDataContext from "components/contexts/merchantSensitiveDataContext";
import {
	SSNNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import { maxPhoneLength15, phoneMinLength8 } from "validators/userValidators";
import {
	dateOfBirth1900,
	dateOfBirthMoreThan18,
	dateValidation,
	emailValid,
	maxLengthSSN,
	minLengthSSN,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDate,
	required,
} from "validators/validation";

import { PoliticallyExposedPersonTitle } from "./ownerHelpers";
import { uniqueSsn } from "./validators";

import styles from "../formSections.module.scss";

const Signer = ({
	signer,
	removeSigner,
	signerValidationInfo,
	formName,
	isBadgeShown,
	sectionName,
}) => {
	const isRequired = useContext(RequiredMarkContext);
	const { isReadOnly } = useContext(MerchantSensitiveDataContext);

	return (
		<FormSection name="signer">
			<Row>
				<Col>
					<div className={styles.wrappOwner}>
						<p>
							Authorized Signer{" "}
							{isBadgeShown && (
								<Badge
									variant="primary"
									value="Signature is required"
									className="ml-3"
								/>
							)}
						</p>

						<Button
							value="Remove signer"
							onClick={removeSigner}
							variant="secondary"
							style={{ marginRight: "-21px" }}
						/>
					</div>
				</Col>
			</Row>
			<Row className="pb-1">
				<Col md={5}>
					<div className="d-flex">
						<Field
							component={CheckButton}
							label="Is a Politically Exposed Person"
							id="politicallyExposedSigner"
							name="isAPoliticallyExposedPerson"
							checked={signer.isAPoliticallyExposedPerson}
							hideRequiredMark
						/>
						<OverlayTrigger title={PoliticallyExposedPersonTitle} />
					</div>
				</Col>
				<Col md={{ span: 5, offset: 2 }}></Col>
			</Row>
			<Row className="pt-3">
				<Col md={5}>
					<Field
						component={CustomInput}
						validate={isRequired ? [required] : undefined}
						label="First Name"
						name="firstName"
						loader={SpinnerLoader}
						disabled={signerValidationInfo.isSsnBeingValidated}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						readOnly={isReadOnly}
						customError={signerValidationInfo.ssnValidationError}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={isRequired ? [required] : undefined}
						label="Last Name"
						name="lastName"
						loader={SpinnerLoader}
						disabled={signerValidationInfo.isSsnBeingValidated}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						readOnly={isReadOnly}
						customError={signerValidationInfo.ssnValidationError}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						validate={isRequired ? [required] : undefined}
						label="Title / Position"
						name="titlePosition"
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}></Col>
			</Row>
			<Row>
				<Col md={5}>
					<Field
						component={PhoneInputRedux}
						label="Home / Mobile Phone"
						name="homeMobilePhone"
						isRequired={isRequired}
						validate={fp.compact([
							isRequired ? required : undefined,
							phoneMinLength8,
							maxPhoneLength15,
						])}
						formName={formName}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={fp.compact([
							isRequired ? required : undefined,
							emailValid,
						])}
						label="Email Address"
						name="emailAddress"
						normalize={normalizeAll([trimMidSentenceNormalizer])}
						normalizeOnBlur={trimNormalizer}
					/>
				</Col>
			</Row>
			<AddressSectionRedux
				formName={formName}
				sectionName={`${sectionName}.signer.address`}
				isRequired={isRequired}
			/>

			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						validate={
							!isReadOnly &&
							fp.compact([
								isRequired ? required : undefined,
								minLengthSSN,
								maxLengthSSN,
								uniqueSsn,
							])
						}
						normalize={SSNNormalizer}
						loader={SpinnerLoader}
						label="SSN"
						name="ssn"
						loading={signerValidationInfo.isSsnBeingValidated}
						customError={signerValidationInfo.ssnValidationError}
						disabled={signerValidationInfo.isSsnBeingValidated}
						readOnly={isReadOnly}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={fp.compact([
							isRequired ? required : undefined,
							dateValidation,
							dateOfBirth1900,
							dateOfBirthMoreThan18,
						])}
						normalize={onlyDate}
						name="dateOfBirth"
						label="Date of Birth"
					/>
				</Col>
			</Row>
		</FormSection>
	);
};

export default memo(Signer);
